import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Backdrop,
  Chip,
  Box,
  Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { CATEGORY_OPTIONS } from '../../constants/feedback';

export default function FeedbackModal({
  open,
  onClose,
  chatId,
  userMessageId,
  userMessage,
  assistantMessageId,
  assistantMessage,
  onSubmitSuccess,
}) {
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => {
    if (open && titleRef.current) {
      setTimeout(() => {
        titleRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const isValid = category && title.trim() && feedbackText.trim();

  const handleSubmit = async () => {
    if (!category) {
      toast.error('Please select a category');
      return;
    }
    if (!title.trim()) {
      toast.error('Please provide a title');
      return;
    }
    if (!feedbackText.trim()) {
      toast.error('Please provide feedback details');
      return;
    }

    setIsSubmitting(true);

    try {
      await api.feedback.submitFeedback({
        title: title.trim(),
        description: feedbackText.trim(),
        category,
        session_id: chatId || undefined,
      });

      toast.success('Feedback submitted successfully!');
      onSubmitSuccess();
      setCategory('');
      setTitle('');
      setFeedbackText('');
      onClose();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error(error.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      BackdropComponent={Backdrop}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          padding: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: 2,
          fontSize: '1.5rem',
          fontWeight: 600,
        }}
      >
        Feedback
        <IconButton
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ paddingTop: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Category pills */}
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Category <span style={{ color: 'red' }}>*</span>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {CATEGORY_OPTIONS.map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                onClick={() => !isSubmitting && setCategory(opt.value)}
                color={category === opt.value ? 'primary' : 'default'}
                variant={category === opt.value ? 'filled' : 'outlined'}
                disabled={isSubmitting}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>

        {/* Title */}
        <TextField
          fullWidth
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Brief title for your feedback"
          disabled={isSubmitting}
          required
          inputRef={titleRef}
          inputProps={{ maxLength: 150 }}
        />

        {/* Description */}
        <TextField
          fullWidth
          multiline
          rows={5}
          label="Description"
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          placeholder="Describe the issue or feedback in detail..."
          disabled={isSubmitting}
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: 'inherit',
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ padding: 3, paddingTop: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
          variant="contained"
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
